import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Divider,
    Skeleton,
    Chip,
    Avatar,
    Tooltip,
    useMediaQuery,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';
import {
    fetchTickets,
    mapPriority,
    mapStatus,
    selectViewedTickets,
    setViewedTickets,
} from '../../redux/ticket/ticket.actions';
import { getUsers } from '../../redux/user/user.actions';
import SearchBar from '../../components/SearchBar';
import AdvancedSearchModal from '../../components/AdvancedSearchModal';
import ServicePicker from '../../components/ServicePicker';
import TableInfo from '../../components/TableInfo';
import { toast } from 'react-toastify';
import {
    applyAdvancedSearchFilters,
    formatTicketClientDisplay,
    textoDeBuscaDoCliente,
    getTicketClosingDate,
    isTicketTerminalClosed,
    ticketBelongsToClient,
} from '../../utils/ticketAdvancedSearchFilters';
import { paiPorServico } from '../../utils/arvoreDeServicos';
import { mesclarChamadosSemRepetir, pedidosDosChamadosDoCliente } from '../../utils/chamadosDoCliente';
import { normalizeTicketAssigneeForList } from '../../utils/ticketAssignee';

const PERMISSION_EDIT_CLIENT = '#editar-dados-cliente';

const formatTicketDateTime = (value) => {
    if (value == null || value === '') return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const formatTicketDateTimeCompact = (value) => {
    if (value == null || value === '') return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    try {
        return d.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return d.toLocaleString('pt-BR');
    }
};

const formatCompanyPhone = (company) => {
    if (!company) return '';
    const area = company.area_code ? String(company.area_code).trim() : '';
    const phone = company.phone ? String(company.phone).trim() : '';
    if (!phone) return '';
    return area ? `(${area}) ${phone}` : phone;
};

const parseCompanyPhoneInput = (input) => {
    const raw = String(input || '').trim();
    if (!raw) return { area_code: null, phone: null };
    const digits = raw.replace(/\D/g, '');
    if (!digits) return { area_code: null, phone: null };
    if (digits.length > 10) {
        return { area_code: digits.slice(0, 2), phone: digits.slice(2) };
    }
    if (digits.length >= 10) {
        return { area_code: digits.slice(0, 2), phone: digits.slice(2) };
    }
    return { area_code: null, phone: digits };
};

const buildFixedClientFilter = (client) => {
    if (!client?.id) return null;
    return {
        id: client.id,
        name: client.name,
        code: client.code ?? client.company?.code ?? null,
        company: client.company || null,
    };
};

const ClientTicketsPage = ({
    fetchTickets,
    getUsers,
    tickets,
    users,
    currentUser,
    permissions,
    setViewedTickets,
    history,
}) => {
    const { clientId } = useParams();
    const { theme } = useContext(ThemeContext);
    const viewedTickets = useSelector(selectViewedTickets);
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const [client, setClient] = useState(null);
    const [loadingClient, setLoadingClient] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advancedFilters, setAdvancedFilters] = useState(null);
    const [services, setServices] = useState([]);
    const [serviceFilter, setServiceFilter] = useState('');
    /** true = lista só tickets “em curso” (exclui resolvido, fechado e cancelado), como filtro ativo */
    const [excludeTerminalStatuses, setExcludeTerminalStatuses] = useState(true);
    /** O histórico deste cliente vindo do servidor -- inclui os finalizados. */
    const [chamadosDoServidor, setChamadosDoServidor] = useState([]);

    const [editName, setEditName] = useState('');
    const [editContactEmail, setEditContactEmail] = useState('');
    const [editCompanyEmail, setEditCompanyEmail] = useState('');
    const [editCompanyPhone, setEditCompanyPhone] = useState('');
    const [editWhatsapp, setEditWhatsapp] = useState('');
    const [savingClient, setSavingClient] = useState(false);
    // A fase do relacionamento da EMPRESA (item 7).
    //
    // As OPCOES vem da api (`/companies/relationship-phases`) e nao de uma
    // lista aqui: com duas copias, a primeira fase nova faria a tela
    // oferecer uma opcao que a api recusa com 422 -- ou o contrario.
    const [editFase, setEditFase] = useState('');
    const [fasesDisponiveis, setFasesDisponiveis] = useState([]);
    const [companyContacts, setCompanyContacts] = useState([]);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [loadingContacts, setLoadingContacts] = useState(false);

    const canEditClient =
        Boolean(currentUser?.is_super_admin) ||
        (Array.isArray(permissions) && permissions.includes(PERMISSION_EDIT_CLIENT));

    useEffect(() => {
        if (!users?.length) getUsers();
    }, [users, getUsers]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        let cancelled = false;
        const id = Number(clientId);
        if (!clientId || Number.isNaN(id) || id <= 0) {
            setLoadError('Cliente inválido');
            setLoadingClient(false);
            return;
        }
        setLoadingClient(true);
        setLoadError(null);
        http.get(`/clients/${id}`)
            .then((res) => {
                if (cancelled) return;
                const c = res.data;
                setClient(c);
                setEditName(c?.name || '');
                setEditContactEmail(c?.email || '');
                setEditCompanyEmail(c?.company?.email || '');
                setEditCompanyPhone(formatCompanyPhone(c?.company) || '');
                setEditFase(c?.company?.relationship_phase || '');
                setEditWhatsapp(c?.whatsapp_user_number || '');
                setSelectedContactId(c?.id || null);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError('Não foi possível carregar o cliente.');
                    toast.error('Não foi possível carregar o cliente.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingClient(false);
            });
        return () => {
            cancelled = true;
        };
    }, [clientId]);

    const companyId = client?.company_id ?? client?.company?.id ?? null;

    useEffect(() => {
        let cancelled = false;
        if (!companyId) {
            setCompanyContacts([]);
            return;
        }
        setLoadingContacts(true);
        http.get(`/client-search/company/${companyId}`)
            .then((res) => {
                if (cancelled) return;
                const payload = res.data;
                const contacts = payload?.data?.clients
                    || (Array.isArray(payload?.data) && payload.data[0]?.clients)
                    || [];
                const normalized = Array.isArray(contacts) ? contacts : [];
                setCompanyContacts(normalized);
                if (normalized.length > 0) {
                    setSelectedContactId((prev) => prev ?? normalized[0].id);
                }
            })
            .catch(() => {
                if (!cancelled) setCompanyContacts([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingContacts(false);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId]);

    useEffect(() => {
        if (!selectedContactId) return;
        const selected = companyContacts.find((c) => Number(c.id) === Number(selectedContactId));
        if (!selected) return;
        setEditName(selected.name || '');
        setEditContactEmail(selected.email || '');
        setEditWhatsapp(selected.whatsapp_user_number || '');
    }, [selectedContactId, companyContacts]);

    /*
     * O histórico DESTE cliente, pedido ao servidor.
     *
     * Antes esta tela só refinava a lista global que a tela de chamados
     * carrega -- "os ativos mais os 100 finalizados mais recentes". Os
     * finalizados de um cliente específico quase nunca estão ali, e por isso
     * incluir cancelados e resolvidos "não gerava" nada (relato de 18/10).
     *
     * São dois pedidos, um por eixo, porque a tela considera do cliente o
     * chamado ligado a ele OU à empresa dele, e o servidor combina os dois
     * campos com E. Ver `utils/chamadosDoCliente`.
     */
    useEffect(() => {
        const pedidos = pedidosDosChamadosDoCliente(clientId, companyId);

        if (pedidos.length === 0) {
            setChamadosDoServidor([]);
            return undefined;
        }

        let cancelado = false;

        Promise.all(pedidos.map((params) => http.get('/ticket/paginated', { params }).catch(() => null)))
            .then((respostas) => {
                if (cancelado) return;

                const linhas = respostas.reduce(
                    (acc, r) => mesclarChamadosSemRepetir(acc, (r?.data?.data || []).map((t) => normalizeTicketAssigneeForList(t))),
                    []
                );

                setChamadosDoServidor(linhas);
            });

        return () => { cancelado = true; };
    }, [clientId, companyId]);

    const baseTickets = useMemo(() => {
        // A lista já carregada vem primeiro: ela é atualizada ao vivo pela
        // tela, e a do servidor é uma foto do momento do pedido.
        const list = mesclarChamadosSemRepetir(tickets?.tickets || [], chamadosDoServidor);
        const cid = Number(clientId);
        if (Number.isNaN(cid) || cid <= 0) return [];
        return list.filter((t) => ticketBelongsToClient(t, cid, companyId));
    }, [tickets, chamadosDoServidor, clientId, companyId]);

    const mapTicketToSearchString = useCallback(
        (ticket) => {
            const statusString = mapStatus(ticket.status);
            const priorityString = mapPriority(ticket.priority);
            const typeString = ticket.is_internal ? 'Interno' : 'Público';
            const protocol = ticket.id != null && ticket.id !== '' ? String(ticket.id) : '';
            const openStr = formatTicketDateTimeCompact(ticket.created_at);
            const closedStr = formatTicketDateTimeCompact(getTicketClosingDate(ticket));
            const sgLabel = ticket.assigned_to?.service_group?.description || '';
            // `textoDeBuscaDoCliente` pelo mesmo motivo da listagem geral: o
            // rótulo escolhe um nome só, e procurar precisa dos três.
            return `${protocol} ${ticket.title} ${ticket.description} ${statusString} ${priorityString} ${ticket.assigned_to?.name || ''} ${sgLabel} ${ticket.created_by?.name || ''} ${textoDeBuscaDoCliente(ticket)} ${ticket.category || ''} ${ticket.contact || ''} ${ticket.channel || ''} ${typeString} ${openStr} ${closedStr}`;
        },
        []
    );

    useEffect(() => {
        http.get('/services')
            .then((r) => setServices(Array.isArray(r.data) ? r.data : []))
            .catch(() => setServices([]));
    }, []);

    const ticketsAfterStatusPreset = useMemo(() => {
        if (!excludeTerminalStatuses) return baseTickets;
        return baseTickets.filter((t) => !isTicketTerminalClosed(t));
    }, [baseTickets, excludeTerminalStatuses]);

    // A mesma árvore que a TicketPage usa: filtrar por um serviço inclui a
    // descendência dele, aqui também. Sem isto, a mesma escolha teria
    // significados diferentes em duas telas do mesmo sistema.
    const paiPorIdDoServico = useMemo(() => paiPorServico(services), [services]);

    const ticketsAfterAdvanced = useMemo(() => {
        if (!advancedFilters) return ticketsAfterStatusPreset;
        // O quarto argumento é `finalizados` (ids dos status finais), que
        // esta tela não carrega -- `ehConcluido` cai na reserva pelo NOME,
        // que o payload traz. O mapa de serviços é o QUINTO: trocar a ordem
        // faria o mapa chegar como lista de status e os dois filtros
        // errarem em silêncio.
        return applyAdvancedSearchFilters(ticketsAfterStatusPreset, advancedFilters, currentUser, [], paiPorIdDoServico);
    }, [ticketsAfterStatusPreset, advancedFilters, currentUser, paiPorIdDoServico]);

    const rows = useMemo(() => {
        let list = ticketsAfterAdvanced;
        if (serviceFilter !== '' && serviceFilter != null) {
            list = list.filter((ticket) => Number(ticket.service_id) === Number(serviceFilter));
        }
        if (searchValue?.trim()) {
            const q = searchValue.toLowerCase();
            list = list.filter((ticket) => mapTicketToSearchString(ticket).toLowerCase().includes(q));
        }
        return list;
    }, [ticketsAfterAdvanced, searchValue, mapTicketToSearchString, serviceFilter]);

    const fixedClientFilter = useMemo(() => buildFixedClientFilter(client), [client]);

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleRowClick = useCallback(
        (ticket) => {
            const exists = viewedTickets.some((t) => t.id === ticket.id);
            if (!exists) {
                const limitReached = localStorage.getItem('quickAccessLimitReached') === 'true';
                if (!limitReached) {
                    setViewedTickets([
                        ...viewedTickets,
                        { id: ticket.id, number: ticket.number, title: ticket.title || 'Sem título' },
                    ]);
                }
            }
            history.push(`/ticket/${ticket.id}`);
        },
        [viewedTickets, history, setViewedTickets]
    );

    const serviceNameById = useMemo(() => {
        const map = {};
        (services || []).forEach((s) => {
            map[String(s.id)] = s.name;
            (s.subservices || []).forEach((sub) => {
                map[String(sub.id)] = `${s.name} → ${sub.name}`;
            });
        });
        return map;
    }, [services]);

    const listColumns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'Prot.',
                flex: 0.35,
                minWidth: 52,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                        {params.row.number}
                    </Typography>
                ),
            },
            {
                field: 'title',
                headerName: 'Assunto',
                flex: 1.2,
                minWidth: 120,
                renderCell: (params) => (
                    <Tooltip title={params.row.title || ''} arrow>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {params.row.title || '—'}
                        </Typography>
                    </Tooltip>
                ),
            },
            {
                field: 'service_id',
                headerName: 'Serviço',
                flex: 0.7,
                minWidth: 100,
                renderCell: (params) => {
                    const label = serviceNameById[String(params.row.service_id)] || '—';
                    return (
                        <Tooltip title={label} arrow>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.72rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {label}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 0.45,
                minWidth: 72,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>
                        {mapStatus(params.row.status)}
                    </Typography>
                ),
            },
            {
                field: 'priority',
                headerName: 'Prio.',
                flex: 0.4,
                minWidth: 64,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>
                        {mapPriority(params.row.priority)}
                    </Typography>
                ),
            },
            {
                field: 'assigned_to',
                headerName: 'Responsável',
                flex: 0.65,
                minWidth: 88,
                renderCell: (params) => {
                    const name = params.row.assigned_to?.name || '—';
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem' }}>{getInitials(name)}</Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {name}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: 'created_at',
                headerName: 'Abertura',
                flex: 0.55,
                minWidth: 96,
                renderCell: (params) => {
                    const compact = formatTicketDateTimeCompact(params.row.created_at);
                    const full = formatTicketDateTime(params.row.created_at);
                    return (
                        <Tooltip title={full || '—'} arrow placement="bottom">
                            <Typography variant="body2" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                {compact || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                field: 'closing_at',
                headerName: 'Fechamento',
                flex: 0.55,
                minWidth: 96,
                sortable: true,
                valueGetter: (params) => {
                    const raw = getTicketClosingDate(params.row);
                    if (!raw) return null;
                    const t = new Date(raw).getTime();
                    return Number.isNaN(t) ? null : t;
                },
                renderCell: (params) => {
                    const raw = getTicketClosingDate(params.row);
                    const compact = formatTicketDateTimeCompact(raw);
                    const full = formatTicketDateTime(raw);
                    return (
                        <Tooltip title={full || 'Sem data de fechamento'} arrow placement="bottom">
                            <Typography variant="body2" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                {compact || '—'}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
        ],
        [serviceNameById]
    );

    // Busca as fases uma vez. Falha em silencio de proposito: sem as
    // opcoes o seletor fica vazio e o resto da tela de cliente continua
    // funcionando -- derrubar a tela por causa de um seletor seria pior.
    useEffect(() => {
        let cancelado = false;
        http.get('/companies/relationship-phases')
            .then(({ data }) => {
                if (!cancelado && Array.isArray(data)) setFasesDisponiveis(data);
            })
            .catch(() => {});
        return () => { cancelado = true; };
    }, []);

    const handleSaveClient = async () => {
        if (!client?.id || !canEditClient) return;
        setSavingClient(true);
        try {
            const targetClientId = selectedContactId ?? client.id;
            const clientPayload = {
                name: editName,
                email: editContactEmail || null,
                whatsapp_user_number: editWhatsapp || null,
            };
            const { data: updatedClient } = await http.put(`/clients/${targetClientId}`, clientPayload);

            let mergedClient = updatedClient;
            const companyId = updatedClient?.company?.id ?? updatedClient?.company_id ?? null;
            if (companyId) {
                const parsedPhone = parseCompanyPhoneInput(editCompanyPhone);
                const companyPayload = {
                    email: editCompanyEmail || null,
                    area_code: parsedPhone.area_code,
                    phone: parsedPhone.phone,
                    // Vazio vira `null`: e assim que o operador DESFAZ uma
                    // fase escolhida errado. String vazia seria recusada
                    // pela validacao da api, que aceita as tres chaves ou
                    // nulo.
                    relationship_phase: editFase || null,
                };
                const { data: updatedCompany } = await http.put(`/companies/${companyId}`, companyPayload);
                mergedClient = { ...updatedClient, company: updatedCompany };
            }

            setClient(mergedClient);
            setCompanyContacts((prev) => prev.map((c) => (
                Number(c.id) === Number(updatedClient.id)
                    ? { ...c, ...updatedClient }
                    : c
            )));
            toast.success('Dados do cliente atualizados.');
        } catch (err) {
            const msg = err.response?.data?.message || 'Não foi possível salvar.';
            toast.error(msg);
        } finally {
            setSavingClient(false);
        }
    };

    if (loadError && !client) {
        return (
            <Box sx={{ p: 3, maxWidth: 560 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/ticket')} sx={{ textTransform: 'none', mb: 2 }}>
                    Voltar aos tickets
                </Button>
                <Typography color="error">{loadError}</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => history.push('/ticket')}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                        Tickets
                    </Button>
                    <ConfirmationNumberOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }} noWrap>
                            Cliente e tickets
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {client?.company?.business_name || client?.company?.name || 'Carregando…'}
                        </Typography>
                    </Box>
                </Box>
                <Chip size="small" label={`${rows.length} ticket(s)`} variant="outlined" />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: isMdUp ? 'row' : 'column',
                    gap: 2,
                    p: 2,
                    overflow: 'hidden',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: '100%', md: 340 },
                        flexShrink: 0,
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                        overflowY: 'auto',
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                        Dados do cliente
                    </Typography>
                    {loadingClient ? (
                        <Skeleton variant="rounded" height={120} />
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Empresa
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {client?.company?.business_name || client?.company?.name || '—'}
                            </Typography>
                            {client?.company?.cnpj && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    CNPJ: {client.company.cnpj}
                                </Typography>
                            )}
                            {client?.company?.code != null && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Código empresa: {client.company.code}
                                </Typography>
                            )}
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Contatos da loja
                            </Typography>
                            {loadingContacts ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Carregando contatos...
                                </Typography>
                            ) : companyContacts.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                    {companyContacts.map((contact) => (
                                        <Chip
                                            key={contact.id}
                                            size="small"
                                            variant={Number(contact.id) === Number(selectedContactId) ? 'filled' : 'outlined'}
                                            color={Number(contact.id) === Number(selectedContactId) ? 'primary' : 'default'}
                                            label={contact.name || `Contato #${contact.id}`}
                                            onClick={() => setSelectedContactId(contact.id)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Nenhum contato vinculado encontrado.
                                </Typography>
                            )}
                            {!canEditClient ? (
                                <>
                                    <Typography variant="caption" color="text.secondary">
                                        Contato
                                    </Typography>
                                    <Typography variant="body2">{editName || '—'}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {editContactEmail || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        WhatsApp (utilizador)
                                    </Typography>
                                    <Typography variant="body2">{editWhatsapp || '—'}</Typography>
                                </>
                            ) : (
                                <>
                                    <TextField
                                        label="Nome do contato selecionado"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    <TextField
                                        label="E-mail do contato selecionado"
                                        value={editContactEmail}
                                        onChange={(e) => setEditContactEmail(e.target.value)}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    <TextField
                                        label="E-mail da loja"
                                        value={editCompanyEmail}
                                        onChange={(e) => setEditCompanyEmail(e.target.value)}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    <TextField
                                        label="Telefone da loja"
                                        value={editCompanyPhone}
                                        onChange={(e) => setEditCompanyPhone(e.target.value)}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    <TextField
                                        label="WhatsApp (utilizador)"
                                        value={editWhatsapp}
                                        onChange={(e) => setEditWhatsapp(e.target.value)}
                                        fullWidth
                                        size="small"
                                        margin="dense"
                                    />
                                    {/*
                                      Fase do relacionamento da EMPRESA (item 7), nao do
                                      contato: os outros campos deste formulario sao do
                                      contato, e por isso o rotulo diz "da loja".

                                      "Nao definida" e opcao de verdade, e nao um placeholder:
                                      e por ela que o operador desfaz uma escolha errada.
                                    */}
                                    <FormControl fullWidth size="small" margin="dense">
                                        <InputLabel id="fase-do-relacionamento">Fase do relacionamento (da loja)</InputLabel>
                                        <Select
                                            labelId="fase-do-relacionamento"
                                            label="Fase do relacionamento (da loja)"
                                            value={editFase}
                                            onChange={(e) => setEditFase(e.target.value)}
                                            inputProps={{ 'aria-label': 'Fase do relacionamento' }}
                                            SelectDisplayProps={{ 'data-testid': 'seletor-da-fase' }}
                                        >
                                            <MenuItem value=""><em>Não definida</em></MenuItem>
                                            {fasesDisponiveis.map((f) => (
                                                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ mt: 1.5, textTransform: 'none' }}
                                        onClick={handleSaveClient}
                                        disabled={savingClient}
                                    >
                                        {savingClient ? 'A guardar…' : 'Guardar alterações'}
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </Paper>

                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 1.5 }}>
                    <SearchBar
                        options={ticketsAfterAdvanced.map((t) => mapTicketToSearchString(t))}
                        onSearchChange={(v) => setSearchValue(v)}
                        sortOrder="asc"
                        placeholder="Pesquisar nestes tickets…"
                        clearSearch={() => {
                            setSearchValue('');
                            setAdvancedFilters(null);
                        }}
                        onAdvancedSearch={() => setAdvancedOpen(true)}
                        sx={{
                            width: '100%',
                            '& .MuiFilledInput-root': {
                                borderRadius: 2,
                                minHeight: 40,
                                backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100',
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        <Box sx={{ width: 240 }}>
                            <ServicePicker
                                services={services}
                                value={serviceFilter}
                                onChange={(nextId) => setServiceFilter(nextId ?? '')}
                                allowClear
                                emptyLabel="Todos os serviços"
                                placeholder="Filtrar por serviço"
                            />
                        </Box>
                        {excludeTerminalStatuses ? (
                            <Chip
                                label="Filtro: sem resolvido, fechado ou cancelado"
                                size="small"
                                color="success"
                                variant="outlined"
                                onDelete={() => setExcludeTerminalStatuses(false)}
                                sx={{ fontWeight: 500 }}
                            />
                        ) : (
                            <Chip
                                label="Todos os status (incl. resolvido, fechado e cancelado)"
                                size="small"
                                variant="outlined"
                                onDelete={() => setExcludeTerminalStatuses(true)}
                                sx={{ fontWeight: 500 }}
                            />
                        )}
                        {advancedFilters && (
                            <Chip
                                label="Busca avançada ativa"
                                size="small"
                                onDelete={() => setAdvancedFilters(null)}
                                variant="outlined"
                                color="primary"
                            />
                        )}
                    </Box>
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.paper',
                            overflow: 'hidden',
                        }}
                    >
                        <TableInfo
                            rows={rows}
                            columns={listColumns}
                            autoHeight
                            fullHeight={false}
                            isBorder={false}
                            color="transparent"
                            density="compact"
                            onRowClick={(params) => handleRowClick(params.row)}
                            getRowHeight={() => 42}
                            headerHeight={44}
                            sx={{
                                '& .MuiDataGrid-columnHeader': { py: 0.25 },
                            }}
                        />
                    </Paper>
                </Box>
            </Box>

            <AdvancedSearchModal
                open={advancedOpen}
                onClose={() => setAdvancedOpen(false)}
                fixedClient={fixedClientFilter}
                scopedTicketsForMeta={baseTickets}
                onApply={(f) => setAdvancedFilters(f)}
                onClear={() => setAdvancedFilters(null)}
            />
        </Box>
    );
};

const mapState = (state) => ({
    tickets: state.tickets,
    users: state.user.users,
    currentUser: state.user.currentUser,
    permissions: state.user.permissions || [],
});

const mapDispatch = (dispatch) => ({
    fetchTickets: () => dispatch(fetchTickets()),
    getUsers: () => dispatch(getUsers()),
    setViewedTickets: (t) => dispatch(setViewedTickets(t)),
});

export default withRouter(connect(mapState, mapDispatch)(ClientTicketsPage));
